<template>
	<div>
		<CommonFlame :title="'工程台帳'" :maxWidth="'auto'">
			<template v-if="!$vuetify.breakpoint.xs" v-slot:app-bar-btn>
				<HelpDialog
					:name="ganttChart ? 'manufactureProcessLedgerChart' : 'manufactureProcessLedgerTable'"
					openBtnColor="primary"
				/>
				<v-btn color="primary" @click="changeView">
					<template v-if="ganttChart">
						<v-icon class="mr-1">mdi-table</v-icon>
						表を表示
					</template>
					<template v-else>
						<v-icon class="mr-1">mdi-chart-timeline</v-icon>
						ガントチャートを表示
					</template>
				</v-btn>
				<ProcessLedgerSearchDialog
					:query.sync="q"
					:categories="categories"
					:largeProcessTypes="largeProcessTypes"
					text
					@search-items="searchOrderIndividualInfos(q)"
				/>
			</template>
			<template v-else v-slot:app-bar-btn>
				<HelpDialog
					:name="ganttChart ? 'manufactureProcessLedgerChart' : 'manufactureProcessLedgerTable'"
					openBtnColor="white"
					onlyIcon
				/>
				<ProcessLedgerSearchDialog
					:query.sync="q"
					:categories="categories"
					:largeProcessTypes="largeProcessTypes"
					text
					@search-items="searchOrderIndividualInfos(q)"
				/>
			</template>
			<v-row no-gutters>
				<template v-if="$vuetify.breakpoint.xs && !ganttChart">
					<v-row no-gutters>
						<v-btn color="secondary" class="ml-auto" @click="changeView">
							<v-icon class="mr-1">mdi-chart-timeline</v-icon>
							ガントチャートを表示
						</v-btn>
					</v-row>
				</template>
			</v-row>
			<div v-if="!ganttChart" class="table-container mt-2">
				<v-menu close-on-click :close-on-content-click="false">
					<template v-slot:activator="{ on, attrs }">
						<v-btn color="secondary" class="mr-2 mb-4" v-bind="attrs" v-on="on">
							<v-icon class="mr-1"> mdi-application-cog-outline</v-icon>表示設定
						</v-btn>
					</template>
					<v-card width="300px">
						<v-app-bar dark color="primary" height="32px" elevation="0">
							<h6 class="text-body-2 font-weight-bold white--text">表示設定</h6>
						</v-app-bar>
						<v-sheet class="pa-2" elevation="0">
							<h6 class="text-body-2 font-weight-bold mb-2">工程の開始・終了日時</h6>
							<v-radio-group
								v-model="displayTableDateType"
								@change="changeDisplayTableDateType"
								hide-details
							>
								<v-radio
									v-for="(type, i) in displayDateTypes"
									:key="i"
									:label="type.name"
									:value="type.value"
								/>
							</v-radio-group>
						</v-sheet>
					</v-card>
				</v-menu>

				<v-data-table
					locale="ja-JP"
					:headers="headers"
					:items="modifiedItems"
					class="elevation-1"
					:mobile-breakpoint="240"
					disable-filtering
					disable-pagination
					disable-sort
					hide-default-footer
					no-data-text="データがありません。"
				>
					<template v-slot:item="{ item }">
						<tr @click="selectItem(item)">
							<td>{{ item.created_at }}</td>
							<td>{{ item.product_number }}</td>
							<td>{{ item.sub_number }}</td>
							<td>{{ item.deadline }}</td>
							<td>{{ item.status }}</td>
							<td>{{ item.product_name }}</td>
							<td>{{ item.category_name }}</td>
							<td>{{ item.measurement }}</td>
							<td>{{ item.quantity }}</td>
							<td>{{ item.start_time }}</td>
							<td>{{ item.end_time }}</td>
							<td>{{ item.vulcanization_situation }}</td>
							<td>{{ item.vulcanization_start }}</td>
							<td>{{ item.vulcanization_end }}</td>
							<td>{{ item.blast_situation }}</td>
							<td>{{ item.blast_start }}</td>
							<td>{{ item.blast_end }}</td>
							<td>{{ item.paint_situation }}</td>
							<td>{{ item.paint_start }}</td>
							<td>{{ item.paint_end }}</td>
							<td>{{ item.test_situation }}</td>
							<td>{{ item.test_start }}</td>
							<td>{{ item.test_end }}</td>
							<td>{{ item.water_situation }}</td>
							<td>{{ item.water_start }}</td>
							<td>{{ item.water_end }}</td>
							<td>{{ item.witness_situation }}</td>
							<td>{{ item.witness_start }}</td>
							<td>{{ item.witness_end }}</td>
							<td>{{ item.wrapping_situation }}</td>
							<td>{{ item.wrapping_start }}</td>
							<td>{{ item.wrapping_end }}</td>
						</tr>
					</template>
				</v-data-table>
			</div>
			<GanttChart
				v-else
				:taskTitle="processIndividually ? '工程' : '製品・製造番号'"
				:categories="processIndividually ? ganttChartInfos : []"
				:tasks="processIndividually ? ganttChartProcesses : ganttChartInfos"
				:categoryNameKey="processIndividually ? 'name' : ''"
				:taskArrayName="taskArrayName"
				:taskNameKey="processIndividually ? 'id' : 'id'"
				:startDateKey="dateTimeKey.startDateKey"
				:endDateKey="dateTimeKey.endDateKey"
				:startTimeKey="dateTimeKey.startTimeKey"
				:endTimeKey="dateTimeKey.endTimeKey"
				:fontSize="fontSize"
				:topSectionPosition="'right'"
				:isWindowSizeCheck="isWindowSizeCheck"
				:isDraggable="false"
				:showDates="processIndividually"
				:categoryRecognitionId="'order_individual_info_id'"
				:taskBarStyle="taskBarStyle"
				:taskBarNameStyle="taskBarNameStyle"
				:colorRecognitionKey="displayDateType == 'all' ? 'time_type' : 'situation'"
				:taskBarColorsByRecognitionKey="taskBarColorsByRecognitionKey"
				:editableRecognitionKey="'situation'"
				:editableByRecognitionKey="editableByRecognitionKey"
				:taskBarType="'bar'"
				:changeDatetimeFlag="authorized"
				:categoryClickable="processIndividually"
				:taskClickable="!processIndividually"
				:display_date.sync="display_date"
				@click-category="processIndividually ? selectItemById($event) : null"
				@click-task="processIndividually ? false : selectItemById($event)"
				@change-datetime="changeDatetime($event)"
			>
				<template v-slot:left-time-btn>
					<v-menu close-on-click :close-on-content-click="false">
						<template v-slot:activator="{ on, attrs }">
							<v-btn color="secondary" class="mr-2 mb-4" v-bind="attrs" v-on="on">
								<v-icon class="mr-1"> mdi-application-cog-outline</v-icon>表示設定
							</v-btn>
						</template>
						<v-card width="300px">
							<v-app-bar dark color="primary" height="32px" elevation="0">
								<h6 class="text-body-2 font-weight-bold white--text">表示設定</h6>
							</v-app-bar>
							<v-sheet class="pa-2" elevation="0">
								<h6 class="text-body-2 font-weight-bold">工程の表示</h6>
								<v-checkbox
									:disabled="displayDateType == 'all'"
									label="各工程を一行づつに別々に表示する"
									v-model="processIndividually"
								/>
								<h6 class="text-body-2 font-weight-bold">工程の開始・終了日時</h6>
								<v-radio-group v-model="displayDateType" @change="changeDisplayDateType">
									<v-radio label="全て" :value="'all'" />
									<v-radio
										v-for="(type, i) in displayDateTypes"
										:key="i"
										:label="type.name"
										:value="type.value"
									/>
								</v-radio-group>
							</v-sheet>
						</v-card>
					</v-menu>
				</template>

				<template v-slot:right-time-btn>
					<v-text-field
						class="ml-2 no-line"
						label="現在表示しているデータ"
						outlined
						dense
						v-model="displayDateName"
						readonly
					/>
				</template>

				<template v-slot:task-top-section="{ showDates, taskTitle }">
					<v-col :cols="showDates ? 4 : 12" class="gantt-task">{{ taskTitle }}</v-col>
					<template v-if="showDates">
						<v-col cols="4" class="gantt-start">開始</v-col>
						<v-col cols="4" class="gantt-end">完了</v-col>
					</template>
				</template>

				<template v-if="$vuetify.breakpoint.xs && ganttChart" v-slot:top-btn>
					<v-row no-gutters>
						<v-btn color="secondary" class="ml-auto mr-2" @click="changeView">
							<v-icon class="mr-1">mdi-table</v-icon>
							表を表示
						</v-btn>
					</v-row>
				</template>
				<template v-slot:app-bar-btn>
					<v-row no-gutters justify="end" align="baseline">
						<template v-if="displayDateType == 'all'">
							<div v-for="(type, i) in displayDateTypes" class="px-1 text-body-2" :key="i">
								<span :style="{ color: taskBarColorsByRecognitionKey[type.value] }"> ● </span>
								{{ type.name }}
							</div>
						</template>
						<template v-else>
							<div
								v-for="(situation, i) in Object.keys(situations)"
								class="px-1 text-body-2"
								:key="i"
							>
								<span :style="{ color: taskBarColorsByRecognitionKey[situation] }"> ● </span>
								{{ situations[situation] }}
							</div>
						</template>
						<v-btn
							class="ml-2"
							color="secondary"
							:disabled="!changedProcesses.length"
							@click="saveChangedProcesses"
							>変更を保存する</v-btn
						>
					</v-row>
				</template>
				<template v-slot:category-name="{ task }">
					<span :style="{ fontSize: '0.85rem' }">{{
						common.productNameWithProductNumber(task, true)
					}}</span>
				</template>
				<template v-slot:task-list="{ task, startDateKey, startTimeKey, endDateKey, endTimeKey }">
					<template v-if="processIndividually && displayDateType != 'all'">
						<v-col cols="4" class="task-name">
							{{ task.large_process_type ? task.large_process_type.name : "エラー" }}
						</v-col>
						<v-hover v-slot="{ hover }" close-delay="200" open-delay="200">
							<v-col cols="4" class="task-time" @click="setPositionByTime(task, startDateKey)">
								{{ common.formatDateWithNullableTime(task, startDateKey, startTimeKey) }}
								<v-btn
									v-if="authorized"
									class="edit-icon-btn"
									v-show="hover"
									small
									icon
									@click.stop="openEditSmallDialog(task)"
									><v-icon>mdi-pencil</v-icon></v-btn
								>
							</v-col>
						</v-hover>
						<v-hover v-slot="{ hover }" close-delay="200" open-delay="200">
							<v-col cols="4" class="task-time" @click="setPositionByTime(task, endDateKey)">
								{{ common.formatDateWithNullableTime(task, endDateKey, endTimeKey) }}
								<v-btn
									v-if="authorized"
									class="edit-icon-btn"
									v-show="hover"
									small
									icon
									@click.stop="openEditSmallDialog(task)"
									><v-icon>mdi-pencil</v-icon></v-btn
								>
							</v-col>
						</v-hover>
					</template>
					<v-col v-else-if="displayDateType == 'all'" cols="12" class="task-name">
						{{ task.large_process_type.name }}
					</v-col>
					<v-col v-else cols="12" class="task-name">
						{{ common.productNameWithProductNumber(task, true) }}
					</v-col>
				</template>

				<template v-slot:task-name="{ task }">
					{{ task.large_process_type ? task.large_process_type.name : "エラー" }}
				</template>

				<template v-slot:hover-card="{ hover, task, hoverPosition, taskLine }">
					<v-card
						v-if="hover"
						width="400px"
						min-width="400px"
						class="pa-2 hover-card"
						:style="hoverCardPosition(hoverPosition)"
					>
						<table width="100%" class="show-dialog-table">
							<LargeProcessInfoUnit
								:tdStyle="tdStyle"
								:fontSize="'0.8rem'"
								:large_process="task"
								:showEditBtn="authorized"
								@click-edit-btn="openTimeEditDialog(task)"
							>
								<template v-if="$vuetify.breakpoint.xs" v-slot:top-record>
									<ShowDetailRecord
										:tdStyle="tdStyle"
										:fontSize="'0.8rem'"
										:columnName="'製造番号'"
										:value="
											common.productNameWithProductNumber(
												taskLine
													? taskLine
													: getOrderIndividualInfoById(task.order_individual_info_id),
												true
											)
										"
									/>
								</template>
							</LargeProcessInfoUnit>
						</table>
					</v-card>
				</template>
			</GanttChart>

			<PaginationModule :pagination="pagination" @get-page="getPage($event)" />
			<OrderIndividualInfoShowDialog
				:id="selectedItem.id"
				:dialog.sync="showDialog"
				:showProcessBtn="!ganttChart"
				@update-info="updateOrderIndividualInfo($event)"
			/>
			<v-menu
				:close-on-content-click="false"
				v-model="editSmallDialog"
				:position-x="editMenu.x"
				:position-y="editMenu.y"
				@click:outside="closeEditSmallDialog()"
			>
				<v-card>
					<v-app-bar dark color="primary" height="36px" elevation="0">
						<v-spacer />
						<v-btn icon @click="closeEditSmallDialog()">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-app-bar>
					<v-sheet elevation="0" class="pa-2">
						<v-form v-model="valid">
							<v-row no-gutters>
								<v-col cols="12" sm="6" class="pr-sm-1">
									<v-menu
										ref="menu"
										:close-on-content-click="false"
										close-on-click
										transition="scale-transition"
										offset-y
										min-width="auto"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:label="labelByDisplayDateType('startDate')"
												:rules="
													selectedProcessByMenu[dateTimeKey.startTimeKey] ? [rules.required] : []
												"
												:hint="
													selectedProcessByMenu[dateTimeKey.startTimeKey]
														? `${labelByDisplayDateType(
																'startTime'
														  )}がある場合は日付も入力が必要です。`
														: ''
												"
												:persistent-hint="
													selectedProcessByMenu[dateTimeKey.startTimeKey] != null &&
													!selectedProcessByMenu[dateTimeKey.startDateKey]
												"
												v-model="selectedProcessByMenu[dateTimeKey.startDateKey]"
												outlined
												dense
												readonly
												v-bind="attrs"
												v-on="on"
												class="mt-4"
											/>
										</template>
										<v-date-picker
											v-model="selectedProcessByMenu[dateTimeKey.startDateKey]"
											no-title
											scrollable
											locale="ja"
										/>
									</v-menu>
								</v-col>
								<v-col cols="12" sm="6" class="pl-sm1">
									<v-text-field
										v-model="selectedProcessByMenu[dateTimeKey.startTimeKey]"
										:label="labelByDisplayDateType('startTime')"
										class="mt-4"
										type="time"
										step="300"
										outlined
										dense
									/>
								</v-col>
							</v-row>
							<v-row no-gutters>
								<v-col cols="12" sm="6" class="pr-sm-1">
									<v-menu
										ref="menu"
										:close-on-content-click="false"
										close-on-click
										transition="scale-transition"
										offset-y
										min-width="auto"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:label="labelByDisplayDateType('endDate')"
												:rules="
													selectedProcessByMenu[dateTimeKey.endTimeKey] ? [rules.required] : []
												"
												:hint="
													selectedProcessByMenu[dateTimeKey.endTimeKey]
														? `${labelByDisplayDateType(
																'endTime'
														  )}がある場合は日付も入力が必要です。`
														: ''
												"
												:persistent-hint="
													selectedProcessByMenu[dateTimeKey.endTimeKey] != null &&
													!selectedProcessByMenu[dateTimeKey.endDateKey]
												"
												v-model="selectedProcessByMenu[dateTimeKey.endDateKey]"
												outlined
												dense
												readonly
												v-bind="attrs"
												v-on="on"
												class="mt-4"
											/>
										</template>
										<v-date-picker
											v-model="selectedProcessByMenu[dateTimeKey.endDateKey]"
											no-title
											scrollable
											locale="ja"
										/>
									</v-menu>
								</v-col>
								<v-col cols="12" sm="6" class="pl-sm1">
									<v-text-field
										v-model="selectedProcessByMenu[dateTimeKey.endTimeKey]"
										:label="labelByDisplayDateType('endTime')"
										class="mt-4"
										type="time"
										step="300"
										outlined
										dense
									/>
								</v-col>
							</v-row>
						</v-form>
						<v-row no-gutters justify="center">
							<v-btn
								color="secondary"
								@click="updateLargeProcessByMenu"
								class="mt-4"
								:disabled="!valid"
							>
								<v-icon class="mr-1">mdi-content-save</v-icon>
								変更
							</v-btn>
						</v-row>
					</v-sheet>
				</v-card>
			</v-menu>
			<LargeProcessTimeEditDialog
				:dialog.sync="timeEditDialog"
				:large_process="selectedLargeProcess"
				@update-large-process="updateDateTime($event)"
			/>
		</CommonFlame>
	</div>
</template>

<script>
import axios from "axios"
import moment from "moment"
import Qs from "qs"
import common from "../../helper/common"
import rules from "../../helper/rules"
import errorHandling from "../../helper/errorHandling"
import authenticate from "../../helper/authenticate"
import help from "../../helper/help.json"
import CommonFlame from "../shared/CommonFlame.vue"
import PaginationModule from "../shared/PaginationModule.vue"
import HelpDialog from "../shared/HelpDialog.vue"
import GanttChart from "../shared/MultipleLineGanttChart.vue"
import OrderIndividualInfoShowDialog from "../order_individual_info/components/OrderIndividualInfoShowDialog.vue"
import ShowDetailRecord from "../shared/ShowDetailRecord.vue"
import LargeProcessTimeEditDialog from "../large_process/components/LargeProcessTimeEditDialog.vue"
import LargeProcessInfoUnit from "../large_process/components/LargeProcessInfoUnit.vue"
import ProcessLedgerSearchDialog from "./components/ProcessLedgerSearchDialog.vue"

export default {
	components: {
		CommonFlame,
		PaginationModule,
		OrderIndividualInfoShowDialog,
		ShowDetailRecord,
		GanttChart,
		LargeProcessInfoUnit,
		ProcessLedgerSearchDialog,
		HelpDialog,
		LargeProcessTimeEditDialog,
	},
	data() {
		return {
			common,
			rules,
			headers: tableHeaders(),
			orderIndividualInfos: [],
			ganttChartInfos: [],
			ganttChartProcesses: [],
			q: {
				sorts: "created_at desc",
			},
			pagination: {},
			categories: [],
			largeProcessTypes: [],
			situations: {},
			authorized: false,
			ganttChart: false,
			isWindowSizeCheck: false,
			selectedLargeProcess: {},
			orderIndividualInfoId: null,
			processIndividually: true,
			selectedProcessByMenu: { id: null },
			editSmallDialog: false,
			timeEditDialog: false,
			editMenu: {
				x: null,
				y: null,
			},
			displayDateType: "scheduled",
			displayDateTypes: [
				{ name: "予定（生産企画）", value: "planned" },
				{ name: "予定（製造）", value: "scheduled" },
				{ name: "実績", value: "result" },
			],
			displayTableDateType: "scheduled",
			dateTimeKey: {
				startTimeKey: "schedule_start_time",
				endTimeKey: "schedule_end_time",
				startDateKey: "schedule_start_date",
				endDateKey: "schedule_end_date",
			},
			tableDateTimeKey: {
				startTimeKey: "schedule_start_time",
				endTimeKey: "schedule_end_time",
				startDateKey: "schedule_start_date",
				endDateKey: "schedule_end_date",
			},
			fontSize: "14px",
			tdStyle: {
				textAlign: "left",
			},
			selectedItem: {
				id: null,
			},
			taskBarStyle: {
				textAlign: "center",
				height: "24px",
				paddingBottom: "4px",
				borderRadius: 0,
			},
			taskBarNameStyle: {
				fontSize: ".9rem",
				color: "black",
			},
			taskBarColorsByRecognitionKey: {
				planned: this.$vuetify.theme.themes.light.process.planned,
				scheduled: this.$vuetify.theme.themes.light.process.scheduled,
				ongoing: this.$vuetify.theme.themes.light.process.ongoing,
				interruption: this.$vuetify.theme.themes.light.process.interruption,
				completion: this.$vuetify.theme.themes.light.process.completion,
				canceled: this.$vuetify.theme.themes.light.process.canceled,
				result: this.$vuetify.theme.themes.light.process.completion,
			},
			editableByRecognitionKey: {
				planned: true,
				scheduled: true,
				ongoing: { right: true, left: false, drag: false },
				interruption: { right: true, left: false, drag: false },
				completion: false,
				canceled: false,
			},
			display_date: null,
			changedProcesses: [],
			showDialog: false,
			valid: false,
			help: help,
		}
	},
	mounted() {
		this.setQuery()
		const page = this.$route.query.page ? this.$route.query.page : 1
		this.fetchOrderIndividualInfos(true, page)
	},

	destroyed() {
		window.removeEventListener("beforeunload", this.confirmSave)
	},
	beforeRouteLeave(to, from, next) {
		if (!this.changedProcesses.length) {
			window.removeEventListener("beforeunload", this.confirmSave)
			next()
			return
		}
		const answer = window.confirm("変更したデータは保存されませんが、よろしいですか？")
		if (answer) {
			window.removeEventListener("beforeunload", this.confirmSave)
			next()
		} else {
			next(false)
		}
	},
	methods: {
		getPage(i) {
			this.fetchOrderIndividualInfos(false, i)
			this.$router.push({
				name: "manufacture_order_individual_info_index",
				query: { ...this.q, page: i },
			})
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			})
		},
		searchOrderIndividualInfos(q) {
			this.q = q
			this.fetchOrderIndividualInfos(false, 1)
			this.$router.push({
				name: "manufacture_order_individual_info_index",
				query: { ...this.q, page: 1 },
			})
		},
		fetchOrderIndividualInfos(first_visit, page) {
			const headers = authenticate.setHeader()
			const params = { q: this.q, page, first_visit }
			const paramsSerializer = (params) => Qs.stringify(params, { arrayFormat: "brackets" })
			const url = `/erp/v1/manufactures/order_individual_infos`
			axios
				.get(url, { headers, params, paramsSerializer })
				.then((res) => {
					this.orderIndividualInfos = res.data.order_individual_infos
					this.pagination = res.data.pagination
					if (first_visit) {
						this.authorized = res.data.authorized
						this.categories = res.data.categories
						this.largeProcessTypes = res.data.large_process_types
						this.situations = res.data.situations
					}
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		selectItem(item) {
			this.selectedItem = item
			this.showDialog = true
		},
		confirmSave(event) {
			event.returnValue = "変更したデータは保存されませんが、よろしいですか？"
		},
		selectItemById(id) {
			const item = this.orderIndividualInfos.find((i) => i.id == id)
			if (!item) return
			this.selectedItem = item
			this.showDialog = true
		},
		openTimeEditDialog(e) {
			const item = this.ganttChartInfos.find((i) => i.id == e.order_individual_info_id)
			if (!item) return
			const process = item.large_processes.find((p) => p.id == e.id)
			this.selectedLargeProcess = process
			this.timeEditDialog = true
		},
		updateLargeProcess(process) {
			let item = this.orderIndividualInfos.find((i) => i.id == process.order_individual_info_id)
			if (!item) return
			const itemIndex = this.orderIndividualInfos.indexOf(item)
			const oldProcess = item.large_processes.find((p) => p.id == process.id)
			const processIndex = item.large_processes.indexOf(oldProcess)
			item.large_processes[processIndex] = process
			this.$set(this.orderIndividualInfos, itemIndex, item)
		},
		findProcessByName(item, processName) {
			return item.large_processes.find((c) => c.large_process_type.name == processName)
		},
		setPositionByTime(task, dateKey) {
			let date = task[dateKey]
			if (!date) return
			this.display_date = moment(date).format("YYYY-MM-DD")
		},
		changeDatetime(newProcess) {
			let params

			// 全てを表示している場合は適当した日時を絞り込む
			if (this.displayDateType == "all") {
				let type = newProcess.id.split("-")[0]
				let id = newProcess.id.split("-")[1]
				let startDateKey
				let endDateKey
				let startTimeKey
				let endTimeKey
				if (type == "planned") {
					startDateKey = "planned_start_date"
					endDateKey = "planned_end_date"
					startTimeKey = "planned_start_time"
					endTimeKey = "planned_end_time"
				} else if (type == "scheduled") {
					startDateKey = "schedule_start_date"
					endDateKey = "schedule_end_date"
					startTimeKey = "schedule_start_time"
					endTimeKey = "schedule_end_time"
				} else if (type == "result") {
					startDateKey = "start_date"
					endDateKey = "end_date"
					startTimeKey = "start_time"
					endTimeKey = "end_time"
				}

				if (newProcess[startTimeKey]) {
					params = {
						[startDateKey]: newProcess.display_start_date,
						[endDateKey]: newProcess.display_end_date,
						[startTimeKey]: newProcess.display_start_time,
						[endTimeKey]: newProcess.display_end_time,
						id: id,
					}
				} else {
					params = {
						[startDateKey]: newProcess.display_start_date,
						[endDateKey]: newProcess.display_end_date,
						id: id,
					}
				}
			} else {
				if (newProcess[this.dateTimeKey.startTimeKey]) {
					params = {
						[this.dateTimeKey.startDateKey]: newProcess[this.dateTimeKey.startDateKey],
						[this.dateTimeKey.endDateKey]: newProcess[this.dateTimeKey.endDateKey],
						[this.dateTimeKey.startTimeKey]: newProcess[this.dateTimeKey.startTimeKey],
						[this.dateTimeKey.endTimeKey]: newProcess[this.dateTimeKey.endTimeKey],
						id: newProcess.id,
					}
				} else {
					params = {
						[this.dateTimeKey.startDateKey]: newProcess[this.dateTimeKey.startDateKey],
						[this.dateTimeKey.endDateKey]: newProcess[this.dateTimeKey.endDateKey],
						id: newProcess.id,
					}
				}
			}
			this.updateDateTime(params)
		},
		updateDateTime(params){
			let oldParams = this.changedProcesses.find((p) => p.id == params.id)
			let paramsIndex = this.changedProcesses.indexOf(oldParams)
			if (oldParams) {
				Object.keys(params).forEach((key) => {
					oldParams[key] = params[key]
				})
				this.changedProcesses[paramsIndex] = oldParams
			} else {
				this.changedProcesses.push(params)
			}

			let process = this.ganttChartProcesses.find((p) => p.id == params.id)
			if (!process) return
			let orderIndividualInfo = this.ganttChartInfos.find(
				(t) => t.id == process.order_individual_info_id
			)
			let index = this.ganttChartProcesses.indexOf(process)
			let orderIndividualInfoIndex = this.ganttChartInfos.indexOf(orderIndividualInfo)
			let processIndex = orderIndividualInfo.large_processes.find((p) => p.id == process.id)
			Object.keys(params).forEach((key) => {
				process[key] = params[key]
			})
			// 全てを表示している場合は、専用の配列も更新
			if (this.displayDateType == "all") {
				let processByTime = process.processes_by_time.find((p) => p.id == newProcess.id)
				let processByTimeIndex = process.processes_by_time.indexOf(processByTime)
				Object.keys(params).forEach((key) => {
					if (key != "id") processByTime[key] = params[key]
				})
				Object.keys(newProcess).forEach((key) => {
					processByTime[key] = newProcess[key]
				})
				process.processes_by_time[processByTimeIndex] = processByTime
			}
			this.ganttChartProcesses[index] = process
			orderIndividualInfo.large_processes[processIndex] = process
			this.ganttChartInfos[orderIndividualInfoIndex] = orderIndividualInfo

			window.addEventListener("beforeunload", this.confirmSave)

		},
		updateOrderIndividualInfo(orderIndividualInfo) {
			let item = this.orderIndividualInfos.find((c) => c.id == orderIndividualInfo.id)
			if (!item) return
			const index = this.orderIndividualInfos.indexOf(item)
			item.sub_number = orderIndividualInfo.sub_number
			item.quantity = orderIndividualInfo.quantity
			item.construction_place = orderIndividualInfo.construction_place
			item.start_date = orderIndividualInfo.start_date
			item.start_time = orderIndividualInfo.start_time
			item.end_date = orderIndividualInfo.end_date
			item.end_time = orderIndividualInfo.end_time
			if (!this.ganttChart) item.large_processes = orderIndividualInfo.large_processes
			this.$set(this.orderIndividualInfos, index, item)
		},
		saveChangedProcesses() {
			const headers = authenticate.setHeader()
			axios
				.put(
					`/erp/v1/bulk_large_processes`,
					{ large_processes: this.changedProcesses },
					{
						headers,
					}
				)
				.then((res) => {
					res.data.forEach((process) => {
						this.updateLargeProcess(process)
					})
					this.changedProcesses = []
					window.removeEventListener("beforeunload", this.confirmSave)
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: "変更を保存しました",
					})
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		changeView() {
			if (this.ganttChart && this.changedProcesses.length) {
				let answer = window.confirm("変更したデータは保存されませんが、よろしいですか？")
				if (answer) {
					this.ganttChart = !this.ganttChart
					this.changedProcesses = []
					this.ganttChartInfos = this.orderIndividualInfos
					this.ganttChartProcesses = this.orderIndividualInfos
						.map((item) => item.large_processes)
						.reduce((a, b) => a.concat(b), [])
					window.removeEventListener("beforeunload", this.confirmSave)
				}
			} else {
				this.ganttChart = !this.ganttChart
			}
		},
		changeDisplayTableDateType(e) {
			if (e == "planned") {
				this.tableDateTimeKey.startDateKey = "planned_start_date"
				this.tableDateTimeKey.endDateKey = "planned_end_date"
				this.tableDateTimeKey.startTimeKey = "planned_start_time"
				this.tableDateTimeKey.endTimeKey = "planned_end_time"
			} else if (e == "scheduled") {
				this.tableDateTimeKey.startDateKey = "schedule_start_date"
				this.tableDateTimeKey.endDateKey = "schedule_end_date"
				this.tableDateTimeKey.startTimeKey = "schedule_start_time"
				this.tableDateTimeKey.endTimeKey = "schedule_end_time"
			} else if (e == "result") {
				this.tableDateTimeKey.startDateKey = "start_date"
				this.tableDateTimeKey.endDateKey = "end_date"
				this.tableDateTimeKey.startTimeKey = "start_time"
				this.tableDateTimeKey.endTimeKey = "end_time"
			}
		},
		changeDisplayDateType(e) {
			if (e == "all") {
				// 全て表示するガントチャート用のタスクの配列を作成
				this.processIndividually = true
				this.ganttChartProcesses = this.ganttChartProcesses.map((process) => {
					process.processes_by_time = [
						{
							...process,
							large_process_type: { name: `${process.large_process_type.name}（生産企画）` },
							time_type: "planned",
							id: `planned-${process.id}`,
							situation: process.situation,
							display_start_time: process.planned_start_time,
							display_end_time: process.planned_end_time,
							display_start_date: process.planned_start_date,
							display_end_date: process.planned_end_date,
						},
						{
							...process,
							large_process_type: { name: `${process.large_process_type.name}（製造）` },
							time_type: "scheduled",
							id: `scheduled-${process.id}`,
							situation: process.situation,
							display_start_time: process.schedule_start_time,
							display_end_time: process.schedule_end_time,
							display_start_date: process.schedule_start_date,
							display_end_date: process.schedule_end_date,
						},
						{
							...process,
							large_process_type: { name: `${process.large_process_type.name}（実績）` },
							time_type: "result",
							id: `result-${process.id}`,
							situation: process.situation,
							display_start_time: process.start_time,
							display_end_time: process.end_time,
							display_start_date: process.start_date,
							display_end_date: process.end_date,
						},
					]
					return process
				})
				this.dateTimeKey.startDateKey = "display_start_date"
				this.dateTimeKey.endDateKey = "display_end_date"
				this.dateTimeKey.startTimeKey = "display_start_time"
				this.dateTimeKey.endTimeKey = "display_end_time"
			} else if (e == "planned") {
				this.dateTimeKey.startDateKey = "planned_start_date"
				this.dateTimeKey.endDateKey = "planned_end_date"
				this.dateTimeKey.startTimeKey = "planned_start_time"
				this.dateTimeKey.endTimeKey = "planned_end_time"
			} else if (e == "scheduled") {
				this.dateTimeKey.startDateKey = "schedule_start_date"
				this.dateTimeKey.endDateKey = "schedule_end_date"
				this.dateTimeKey.startTimeKey = "schedule_start_time"
				this.dateTimeKey.endTimeKey = "schedule_end_time"
			} else if (e == "result") {
				this.dateTimeKey.startDateKey = "start_date"
				this.dateTimeKey.endDateKey = "end_date"
				this.dateTimeKey.startTimeKey = "start_time"
				this.dateTimeKey.endTimeKey = "end_time"
			}
		},
		setQuery() {
			const query = this.$route.query
			Object.keys(this.$route.query).forEach((query_key) => {
				if (query[query_key] && query_key != "page") {
					this.q[query_key] = this.formatQuery(query[query_key])
				}
			})
		},
		openEditSmallDialog(process) {
			this.selectedProcessByMenu = {
				id: process.id,
				[this.dateTimeKey.startDateKey]: process[this.dateTimeKey.startDateKey],
				[this.dateTimeKey.endDateKey]: process[this.dateTimeKey.endDateKey],
				[this.dateTimeKey.startTimeKey]: process[this.dateTimeKey.startTimeKey]
					? moment(process[this.dateTimeKey.startTimeKey]).format("HH:mm")
					: null,
				[this.dateTimeKey.endTimeKey]: process[this.dateTimeKey.endTimeKey]
					? moment(process[this.dateTimeKey.endTimeKey]).format("HH:mm")
					: null,
			}
			this.editSmallDialog = true
			this.editMenu = {
				y: event.clientY,
				x: event.clientX,
			}
		},
		updateLargeProcessByMenu() {
			if (this.selectedProcessByMenu[this.dateTimeKey.startTimeKey])
				this.selectedProcessByMenu[this.dateTimeKey.startTimeKey] = `2000-01-01 ${
					this.selectedProcessByMenu[this.dateTimeKey.startTimeKey]
				}`
			if (this.selectedProcessByMenu[this.dateTimeKey.endTimeKey])
				this.selectedProcessByMenu[this.dateTimeKey.endTimeKey] = `2000-01-01 ${
					this.selectedProcessByMenu[this.dateTimeKey.endTimeKey]
				}`
			this.changeDatetime(this.selectedProcessByMenu)
			this.closeEditSmallDialog()
		},
		closeEditSmallDialog() {
			this.editSmallDialog = false
			this.selectedProcessByMenu = { id: null }
		},
		formatQuery(query) {
			if (query instanceof Array) {
				return query.map((v) => (isFinite(v) ? Number(v) : v))
			} else if (isFinite(query)) {
				return Number(query)
			} else {
				return query
			}
		},
		labelByDisplayDateType(key) {
			let labels
			if (this.displayDateType === "planned") {
				labels = {
					startTime: "生産企画予定開始時間",
					endTime: "生産企画予定終了時間",
					startDate: "生産企画予定開始日",
					endDate: "生産企画予定終了日",
				}
			} else if (this.displayDateType === "result") {
				labels = {
					startTime: "開始時間",
					endTime: "終了時間",
					startDate: "開始日",
					endDate: "終了日",
				}
			} else if (this.displayDateType === "scheduled") {
				labels = {
					startTime: "製造部予定開始時間",
					endTime: "製造部予定終了時間",
					startDate: "製造部予定開始日",
					endDate: "製造部予定終了日",
				}
			} else {
				labels = {}
			}
			return labels[key]
		},
		getOrderIndividualInfoById(order_individual_info_id) {
			const orderIndividualInfo = this.orderIndividualInfos.find(
				(item) => item.id == order_individual_info_id
			)
			return orderIndividualInfo
		},
	},
	watch: {
		orderIndividualInfos(newVal, _) {
			this.ganttChartInfos = newVal
			this.ganttChartProcesses = newVal
				.map((item) => item.large_processes)
				.reduce((a, b) => a.concat(b), [])
		},
	},
	computed: {
		taskArrayName() {
			if (this.processIndividually) {
				return this.displayDateType == "all" ? "processes_by_time" : null
			} else {
				return "large_processes"
			}
		},
		displayDateName() {
			if (this.displayDateType === "planned") {
				return "予定(生産企画)"
			} else if (this.displayDateType === "result") {
				return "実績"
			} else if (this.displayDateType === "scheduled") {
				return "予定(製造部)"
			} else {
				return ""
			}
		},
		modifiedItems: {
			get() {
				return this.orderIndividualInfos.map((item) => {
					const ordered_product = item.ordered_product ? item.ordered_product : item.matter_product
					const waterAssociationTest = this.findProcessByName(item, "水協検査")
					const witnessTest = this.findProcessByName(item, "立会検査")
					const vulcanization = this.findProcessByName(item, "加硫")
					const test = this.findProcessByName(item, "社内検査")
					const blast = this.findProcessByName(item, "ブラスト")
					const paint = this.findProcessByName(item, "塗装")
					const wrapping = this.findProcessByName(item, "梱包")
					return {
						id: item.id,
						number: "",
						created_at: common.formatDateTime(item.created_at),
						product_number: item.ordered_product
							? item.ordered_product.product_number
							: "未受注商品",
						sub_number: common.returnOrderIndividualInfo(item),
						status: item.status_i18n,
						deadline: common.formatDate(item?.delivery_info?.deadline),
						product_name: ordered_product.product.name,
						category_name: ordered_product.product.product_category.name,
						measurement: ordered_product.product.measurement,
						quantity: item.quantity,
						start_time: common.formatDateWithNullableTime(
							item,
							"start_date",
							"planned_start_date",
							"start_time",
							"planned_start_time"
						),
						end_time: common.formatDateWithNullableTime(
							item,
							"end_date",
							"planned_end_date",
							"end_time",
							"planned_edn_date"
						),
						vulcanization_situation: vulcanization?.situation_i18n,
						vulcanization_start: common.formatDateWithNullableTime(
							vulcanization,
							this.tableDateTimeKey.startDateKey,
							this.tableDateTimeKey.startTimeKey
						),
						vulcanization_end: common.formatDateWithNullableTime(
							vulcanization,
							this.tableDateTimeKey.endDateKey,
							this.tableDateTimeKey.endTimeKey
						),
						blast_situation: blast?.situation_i18n,
						blast_start: common.formatDateWithNullableTime(
							blast,
							this.tableDateTimeKey.startDateKey,
							this.tableDateTimeKey.startTimeKey
						),
						blast_end: common.formatDateWithNullableTime(
							blast,
							this.tableDateTimeKey.endDateKey,
							this.tableDateTimeKey.endTimeKey
						),
						paint_situation: paint?.situation_i18n,
						paint_start: common.formatDateWithNullableTime(
							paint,
							this.tableDateTimeKey.startDateKey,
							this.tableDateTimeKey.startTimeKey
						),
						paint_end: common.formatDateWithNullableTime(
							paint,
							this.tableDateTimeKey.endDateKey,
							this.tableDateTimeKey.endTimeKey
						),
						test_situation: test?.situation_i18n,
						test_start: common.formatDateWithNullableTime(
							test,
							this.tableDateTimeKey.startDateKey,
							this.tableDateTimeKey.startTimeKey
						),
						test_end: common.formatDateWithNullableTime(
							test,
							this.tableDateTimeKey.endDateKey,
							this.tableDateTimeKey.endTimeKey
						),
						water_situation: waterAssociationTest?.situation_i18n,
						water_start: common.formatDateWithNullableTime(
							waterAssociationTest,
							this.tableDateTimeKey.startDateKey,
							this.tableDateTimeKey.startTimeKey
						),
						water_end: common.formatDateWithNullableTime(
							waterAssociationTest,
							this.tableDateTimeKey.endDateKey,
							this.tableDateTimeKey.endTimeKey
						),
						witness_situation: witnessTest?.situation_i18n,
						witness_start: common.formatDateWithNullableTime(
							witnessTest,
							this.tableDateTimeKey.startDateKey,
							this.tableDateTimeKey.startTimeKey
						),
						witness_end: common.formatDateWithNullableTime(
							witnessTest,
							this.tableDateTimeKey.endDateKey,
							this.tableDateTimeKey.endTimeKey
						),
						wrapping_situation: wrapping?.situation_i18n,
						wrapping_start: common.formatDateWithNullableTime(
							wrapping,
							this.tableDateTimeKey.startDateKey,
							this.tableDateTimeKey.startTimeKey
						),
						wrapping_end: common.formatDateWithNullableTime(
							wrapping,
							this.tableDateTimeKey.endDateKey,
							this.tableDateTimeKey.endTimeKey
						),
					}
				})
			},
			set() {
				return
			},
			keyByDisplayDateType() {
				if (this.displayDateType === "planned") {
					return {
						startTimeKey: "planned_start_time",
						endTimeKey: "planned_end_time",
						startDateKey: "planned_start_date",
						endDateKey: "planned_end_date",
					}
				} else if (this.displayDateType === "result") {
					return {
						startTimeKey: "start_time",
						endTimeKey: "end_time",
						startDateKey: "start_date",
						endDateKey: "end_date",
					}
				} else if (this.displayDateType === "scheduled") {
					return {
						startTimeKey: "scheduled_start_time",
						endTimeKey: "scheduled_end_time",
						startDateKey: "scheduled_start_date",
						endDateKey: "scheduled_end_date",
					}
				} else {
					return {}
				}
			},
		},
		hoverCardPosition() {
			return (hoverPosition) => {
				let hover = {}
				if (this.$vuetify.breakpoint.xsOnly) {
					hover.left = `0px`
					hover.bottom = `0px`
					return hover
				}

				hover.left = `${hoverPosition.pageX}px`
				if (window.innerHeight / 3 > hoverPosition.clientY) {
					hover.top = `${hoverPosition.clientY}px`
				} else if ((window.innerHeight / 3) * 2 > hoverPosition.clientY) {
					hover.top = `${window.innerHeight / 3}px`
				} else {
					hover.bottom = `0px`
				}
				return hover
			}
		},
	},
}

function tableHeaders() {
	return [
		{
			text: "情報物件入力日",
			value: "created_at",
			width: "160px",
		},
		{
			text: "製番",
			value: "product_number",
			width: "160px",
		},
		{
			text: "識別番号",
			value: "sub_number",
			width: "90px",
		},
		{
			text: "納期",
			value: "deadline",
			width: "120px",
		},
		{
			text: "状況",
			value: "status",
			width: "90px",
		},
		{
			text: "品名",
			value: "product_name",
			width: "300px",
		},
		{
			text: "分類（カテゴリー）",
			value: "category_name",
			width: "180px",
		},
		{
			text: "寸法",
			value: "measurement",
			width: "180px",
		},
		{
			text: "数量",
			value: "quantity",
			width: "80px",
		},
		{
			text: "製造開始日時",
			value: "start_time",
			width: "210px",
		},
		{
			text: "製造終了日時",
			value: "end_time",
			width: "210px",
		},
		{
			text: "状況(加硫)",
			value: "vulcanization_situation",
			width: "160px",
		},
		{
			text: "工程開始日時(加硫)",
			value: "vulcanization_start",
			width: "210px",
		},
		{
			text: "工程終了日時(加硫)",
			value: "vulcanization_end",
			width: "210px",
		},
		{
			text: "状況(ブラスト)",
			value: "blast_situation",
			width: "160px",
		},
		{
			text: "工程開始日時(ブラスト)",
			value: "blast_start",
			width: "210px",
		},
		{
			text: "工程終了日時(ブラスト)",
			value: "blast_end",
			width: "210px",
		},
		{
			text: "状況(塗装)",
			value: "paint_situation",
			width: "160px",
		},
		{
			text: "工程開始日時(塗装)",
			value: "paint_start",
			width: "210px",
		},
		{
			text: "工程終了日時(塗装)",
			value: "paint_end",
			width: "210px",
		},
		{
			text: "状況(社内検査)",
			value: "test_situation",
			width: "160px",
		},
		{
			text: "工程開始日時(社内検査)",
			value: "test_start",
			width: "210px",
		},
		{
			text: "工程終了日時(社内検査)",
			value: "test_end",
			width: "210px",
		},
		{
			text: "状況(水協)",
			value: "water_situation",
			width: "160px",
		},
		{
			text: "工程開始日時(水協)",
			value: "water_start",
			width: "210px",
		},
		{
			text: "工程終了日時(水協)",
			value: "water_end",
			width: "210px",
		},
		{
			text: "状況(立会)",
			value: "witness_situation",
			width: "160px",
		},
		{
			text: "工程開始日時(立会)",
			value: "witness_start",
			width: "210px",
		},
		{
			text: "工程終了日時(立会)",
			value: "witness_end",
			width: "210px",
		},
		{
			text: "状況(梱包)",
			value: "wrapping_situation",
			width: "160px",
		},
		{
			text: "工程開始日時(梱包)",
			value: "wrapping_start",
			width: "210px",
		},
		{
			text: "工程終了日時(梱包)",
			value: "wrapping_end",
			width: "210px",
		},
		// {
		// 	text: "納期",
		// 	value: "delivery_info_deadline",
		// 	width: "160px",
		// },
		// {
		// 	text: "オーダー発行日",
		// 	value: "ordered_product_created_at",
		// 	width: "160px",
		// },
	]
}
</script>

<style lang="scss" scoped>
.hover-card {
	z-index: 100;
	position: fixed;
}
.task-time {
	position: relative;
	text-align: center;
	font-size: 0.85rem;
	border-right: solid 1px #80808045;
	.edit-icon-btn {
		position: absolute;
		top: 4px;
		right: 4px;
	}
}
</style>